import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Stack, Text } from '@chakra-ui/react';
import { LuLock } from 'react-icons/lu';
import { usePermission } from '@/shared/lib/auth/permissions';

/* Etap 35 slice 6 — route-level permission gate. Reactive (subscribes to the
 * auth store via usePermission), so a permission revoked while the admin is
 * SITTING on a gated page swaps it to "no access" immediately — driven by
 * useLivePermissionSync's SSE-triggered /v1/me re-fetch, not a page reload.
 *
 * This is UX only. The real boundary is the backend's RequirePermission<P>;
 * this just avoids showing a page that would 403 on every action. */
export function PermissionGate({
  permission,
  children,
}: {
  permission: string;
  children: ReactNode;
}) {
  const allowed = usePermission(permission);
  if (!allowed) return <NoAccess />;
  return <>{children}</>;
}

function NoAccess() {
  const { t } = useTranslation();
  return (
    <Flex align="center" justify="center" h="100%" p="10">
      <Stack align="center" gap="3" color="fg.muted" textAlign="center">
        <LuLock size={32} />
        <Text fontSize="lg" fontWeight="medium" color="fg">
          {t('common.no_access_title')}
        </Text>
        <Text fontSize="sm">{t('common.no_access_body')}</Text>
      </Stack>
    </Flex>
  );
}
